import { useEffect, useState } from 'react';
import { Spinner } from '../../components/spinner/spinner';
import { Location } from '../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { CustomerOrder } from '../../clients/customer-api/order/customer-order';
import { DateTimePicker } from '../../components/date-picker/date-time-picker';
import { DatePicker } from '../../components/date-picker/date-picker';
import { DateTime, Duration, Interval } from 'luxon';

const defaultStep = Duration.fromObject({ minutes: 30 });

export const OrderRequestForm = ({ orderId, requestType }: { orderId: number; requestType: 'extend' | 'call-off' }) => {
  const { getLocationById, getOrderById } = useCustomerAPI();
  const [location, setLocation] = useState<Location>();
  const [disabledDaysOfWeek, setDisabledDaysOfWeek] = useState<number[]>([]);
  const [order, setOrder] = useState<CustomerOrder>();
  const [requestDateTimes, setRequestDateTimes] = useState(new Map<number, DateTime>());

  useEffect(() => {
    const refreshOrder = async () => {
      const order = await getOrderById(orderId);
      setOrder(order);
    };

    setOrder(undefined);
    refreshOrder();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      const refreshLocation = async () => {
        const location = await getLocationById(order.locationId);
        setLocation(location);
        const disabledDaysOfWeek = Array.from(Array(7).keys())
          .map((i) => i + 1)
          .filter((i) => !location.hoursOfOperation.find((ho) => ho.numericDayOfWeek === i));
        setDisabledDaysOfWeek(disabledDaysOfWeek);
      };

      setLocation(undefined);
      refreshLocation();
    }
  }, [order]);

  return order && location ? (
    <div className="order-extend-form">
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th>{requestType === 'extend' ? 'Extend To' : 'Call-off'}</th>
          </tr>
        </thead>
        <tbody>
          {order.products.map((op) => {
            const rentalTimeSlots = op.product.rentalTimeSlots.length
              ? op.product.rentalTimeSlots
              : location.rentalTimeSlots;
            const hours =
              requestDateTimes.get(op.id) && op.rentalEndDate?.diff(requestDateTimes.get(op.id)!).milliseconds !== 0
                ? location.hoursOfOperation.find((ho) => ho.numericDayOfWeek === op.rentalEndDate?.weekday)?.rentalEnd
                : undefined;
            let disabledRange;
            if (requestType === 'extend') {
              disabledRange = [
                Interval.fromDateTimes(
                  DateTime.fromSeconds(0),
                  op.rentalEndDate || DateTime.local({ zone: location.timezone }),
                ),
              ];
            } else {
              disabledRange = [
                Interval.fromDateTimes(DateTime.fromSeconds(0), op.rentalStartDate),
                Interval.fromDateTimes(
                  op.rentalEndDate?.plus(1) || DateTime.local({ zone: location.timezone }),
                  DateTime.local({ zone: location.timezone }).plus({ years: 1000 }),
                ),
              ];
            }
            return (
              <tr key={op.id}>
                <td>
                  <div>{op.product.name}</div>
                  <div>
                    {op.rentalStartDate.toFormat('D t')} to {op.rentalEndDate?.toFormat('D t') || 'returned'}
                  </div>
                </td>
                <td>
                  {location.showRentalTimeSelect ? (
                    <DateTimePicker
                      name={`rental_end_date_time[${op.id}]`}
                      value={requestDateTimes.get(op.id) || op.rentalEndDate}
                      onChange={(v) =>
                        setRequestDateTimes((prev) => {
                          v ? prev.set(op.id, v) : prev.delete(op.id);
                          return new Map(prev);
                        })
                      }
                      disabledDaysOfWeek={disabledDaysOfWeek}
                      disabledIntervals={disabledRange}
                      step={defaultStep}
                      minTime={hours?.start}
                      maxTime={hours?.end}
                      restrictToTimes={rentalTimeSlots.map((ts) => ts.end)}
                      timezone={location.timezone}
                    />
                  ) : (
                    <DatePicker
                      name={`rental_end_date_time[${op.id}]`}
                      value={requestDateTimes.get(op.id) || op.rentalEndDate}
                      onChange={(v) =>
                        setRequestDateTimes((prev) => {
                          v ? prev.set(op.id, v) : prev.delete(op.id);
                          return new Map(prev);
                        })
                      }
                      disabledDaysOfWeek={disabledDaysOfWeek}
                      disabledIntervals={disabledRange}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <Spinner />
  );
};
